import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const backend = process.env.REACT_APP_BACKEND_URL;
const origin = process.env.REACT_APP_SITE_URL;
if (!origin || !backend) throw new Error('SEO site and backend URLs must be configured');

const privatePage = path => /^\/(admin|content-preview|design-vergleich|profile|confirm|unsubscribe|profil-verwaltung)(\/|$)/.test(path);
const privateMetadata = { title:'AgrarKapital', description:'Persönlicher Bereich von AgrarKapital.', robots:'noindex,nofollow' };

const setMeta = (key, value, attribute = 'name') => {
  const matches = [...document.head.querySelectorAll(`meta[${attribute}="${key}"]`)];
  const first = matches.shift();
  matches.forEach(node => node.remove());
  if (!value) { first?.remove(); return; }
  const node = first || document.createElement('meta');
  node.setAttribute(attribute, key);
  if (node.getAttribute('content') !== value) node.setAttribute('content', value);
  if (!first) document.head.appendChild(node);
};

const applyMetadata = meta => {
  const titles = [...document.head.querySelectorAll('title')];
  titles.slice(1).forEach(node => node.remove());
  if (document.title !== meta.title) document.title = meta.title;
  setMeta('description', meta.description);
  setMeta('robots', meta.robots);
  const links = [...document.head.querySelectorAll('link[rel="canonical"]')];
  const first = links.shift();
  links.forEach(node => node.remove());
  if (meta.canonical) {
    const node = first || document.createElement('link');
    node.setAttribute('rel', 'canonical');
    if (node.getAttribute('href') !== meta.canonical) node.setAttribute('href', meta.canonical);
    if (!first) document.head.appendChild(node);
  } else first?.remove();
  for (const [key, value] of Object.entries({ 'og:title':meta.title, 'og:description':meta.description,
    'og:url':meta.canonical, 'og:type':meta.kind || 'website', 'og:site_name':'AgrarKapital', 'og:locale':'de_DE' })) setMeta(key, value, 'property');
  for (const [key, value] of Object.entries({ 'twitter:title':meta.title, 'twitter:description':meta.description,
    'twitter:url':meta.canonical, 'twitter:card':meta.image ? 'summary_large_image' : 'summary' })) setMeta(key, value);
  // A previous property's social photo must not leak into another page.
  setMeta('og:image', meta.image, 'property');
  setMeta('twitter:image', meta.image);
  const schema = document.head.querySelector('#ak-page-structured-data');
  if (meta.structured_data?.length) {
    const node = schema || document.createElement('script');
    node.id = 'ak-page-structured-data';
    node.type = 'application/ld+json';
    const content = JSON.stringify({'@context':'https://schema.org','@graph':meta.structured_data});
    if (node.textContent !== content) node.textContent = content;
    if (!schema) document.head.appendChild(node);
  } else schema?.remove();
};

export const SeoMetadata = () => {
  const { pathname } = useLocation();
  const [resolved, setResolved] = useState(null);
  useEffect(() => {
    const controller = new AbortController();
    if (privatePage(pathname)) {
      setResolved({ path:pathname, metadata:privateMetadata });
      return () => controller.abort();
    }
    fetch(`${backend}/api/seo/page?path=${encodeURIComponent(pathname)}`, { signal:controller.signal })
      .then(response => { if (!response.ok) throw new Error('SEO metadata unavailable'); return response.json(); })
      .then(metadata => { if (!controller.signal.aborted) setResolved({ path:pathname, metadata }); })
      .catch(error => {
        if (error.name !== 'AbortError') {
          console.warn('SEO-Metadaten vorübergehend nicht verfügbar.');
          setResolved({ path:pathname, metadata:{ title:'AgrarKapital', description:'Agrarflächen und landwirtschaftliche Immobilien.', robots:'noindex,follow' } });
        }
      });
    return () => controller.abort();
  }, [pathname]);

  useLayoutEffect(() => {
    const meta = privatePage(pathname) ? privateMetadata : resolved?.path === pathname ? resolved.metadata : null;
    if (!meta) return;
    applyMetadata(meta);
    // Older pages still use Helmet and document.title. Keep one authoritative
    // set of tags across lazy mounts, SPA navigation and delayed data loads.
    const observer = new MutationObserver(() => {
      observer.disconnect();
      applyMetadata(meta);
      observer.observe(document.head, { childList:true, subtree:true, attributes:true, characterData:true });
    });
    observer.observe(document.head, { childList:true, subtree:true, attributes:true, characterData:true });
    return () => observer.disconnect();
  }, [pathname, resolved]);
  return null;
};